"use client";

import React, {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState
} from "react";
import { Button } from "../../atoms/button/button";
import { TimeIndicator } from "../../molecules/time-indicator/time-indicator";
import { LINK_POSITION_BODY, fireLinkClick } from "../../services/analytics";
import styles from "./content-card-carousel.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/* Prev/Next arrow icons — the same four Pagination defaults, duplicated here
 * because the carousel no longer composes Pagination. Kept as local constants
 * rather than a shared export: this is the only other caller today, and the
 * arrows never leaked out of Pagination's public API to begin with. */
const PREV_ICON_ENABLED =
  "https://www.directv.com/assets/content/dsu9ojsbzo/svg/icon-left-arrow-eclipse-neutral50.svg";
const PREV_ICON_DISABLED =
  "https://www.directv.com/assets/content/oiydvklefb/svg/icon-left-arrow-eclipse-slate400.svg";
const NEXT_ICON_ENABLED =
  "https://www.directv.com/assets/content/tfrdumw0o8/svg/icon-right-arrow-eclipse-neutral50.svg";
const NEXT_ICON_DISABLED =
  "https://www.directv.com/assets/content/kw7jrirn0g/svg/icon-right-arrow-eclipse-slate400.svg";

const CLONE_ATTR = "data-content-card-carousel-clone";
const REAL_CARD_SELECTOR =
  `[data-content-card-carousel-card]:not([${CLONE_ATTR}]):not([${CLONE_ATTR}] *)`;

/* The first real card's rendered width plus the track's column gap. Resize-
 * safe — reads live DOM — so a single advance never desynchronises with CSS. */
function getCardWidth(track: HTMLElement): number {
  const first = track.querySelector<HTMLElement>(REAL_CARD_SELECTOR);
  if (!first) {
    return 0;
  }
  const gap = parseFloat(getComputedStyle(track).columnGap || "0") || 0;
  return first.offsetWidth + gap;
}

/* Distance between a real card and its clone: real card count × card width.
 * Zero while no clone strip is mounted, so every loop path is a no-op then. */
function getLoopPeriod(track: HTMLElement): number {
  if (!track.querySelector(`[${CLONE_ATTR}="true"]`)) {
    return 0;
  }
  return track.querySelectorAll(REAL_CARD_SELECTOR).length * getCardWidth(track);
}

/* SVG inner markup for the small play/pause glyphs `VideoWithPause` renders.
 * Keep in sync with `PlayGlyphSmall` / `PauseGlyphSmall` below. */
const CLONE_VIDEO_PLAY_GLYPH =
  "<path d=\"M5 3v10l8-5z\" fill=\"currentColor\"></path>";
const CLONE_VIDEO_PAUSE_GLYPH =
  "<rect fill=\"currentColor\" height=\"10\" rx=\"0.75\" width=\"2.5\" x=\"4\" y=\"3\"></rect>" +
  "<rect fill=\"currentColor\" height=\"10\" rx=\"0.75\" width=\"2.5\" x=\"9.5\" y=\"3\"></rect>";

function playQuietly(video: HTMLVideoElement): void {
  const result = video.play();
  /* jsdom returns undefined; a browser returns a Promise that rejects under
   * autoplay policy. */
  if (result && typeof (result as Promise<void>).catch === "function") {
    (result as Promise<void>).catch(() => undefined);
  }
}

function findVideoButton(card: HTMLElement): HTMLButtonElement | null {
  /* `VideoWithPause`'s button is the only one whose label ends " video"; the
   * card CTA is an <a>. */
  return Array.from(card.querySelectorAll<HTMLButtonElement>("button"))
    .find(btn => (btn.getAttribute("aria-label") || "").endsWith(" video")) || null;
}

/* A clone's <video> is a mirror of its real card's, never an independent
 * player. The real `VideoWithPause` owns playback — reduced-motion gating
 * included — and the clone follows its play/pause events, so a pause on
 * either copy holds when the loop swaps one for the other. The clone's
 * button forwards its click to the real button, and the clone only decodes
 * while it is on screen; its `src` is attached on first play so the clone
 * strip costs no request until the loop actually reaches it. Returns the
 * teardown for the listeners it adds to the real video. */
function mirrorCloneVideo(
  real: HTMLElement,
  clone: HTMLElement,
  observer: IntersectionObserver | null,
  visible: WeakMap<Element, boolean>
): (() => void) | null {
  const realVideo = real.querySelector<HTMLVideoElement>("video");
  const cloneVideo = clone.querySelector<HTMLVideoElement>("video");
  if (!realVideo || !cloneVideo) {
    return null;
  }
  const src = cloneVideo.getAttribute("src") || "";
  cloneVideo.removeAttribute("src");
  /* The real card renders its poster <img> only while paused; the copy taken
   * here may hold one, which would sit over the playing clone video. */
  const posterImg = cloneVideo.previousElementSibling instanceof HTMLImageElement ?
    cloneVideo.previousElementSibling :
    null;
  const realBtn = findVideoButton(real);
  const cloneBtn = findVideoButton(clone);
  const svg = cloneBtn?.querySelector<SVGElement>("svg") || null;
  const title = (cloneBtn?.getAttribute("aria-label") || "")
    .replace(/^(Play|Pause) /, "")
    .replace(/ video$/, "");

  const sync = () => {
    const { paused } = realVideo;
    /* No observer (old browser, jsdom): treat the clone as always visible. */
    const onScreen = observer ? visible.get(cloneVideo) === true : true;
    if (!paused && onScreen) {
      if (!cloneVideo.getAttribute("src") && src) {
        cloneVideo.setAttribute("src", src);
      }
      if (cloneVideo.paused) {
        playQuietly(cloneVideo);
      }
    } else if (!cloneVideo.paused) {
      cloneVideo.pause();
    }
    if (posterImg) {
      posterImg.hidden = !paused;
    }
    if (svg) {
      svg.innerHTML = paused ? CLONE_VIDEO_PLAY_GLYPH : CLONE_VIDEO_PAUSE_GLYPH;
    }
    cloneBtn?.setAttribute("aria-label", `${paused ? "Play" : "Pause"} ${title} video`);
  };

  cloneBtn?.addEventListener("click", event => {
    event.preventDefault();
    event.stopPropagation();
    realBtn?.click();
  });
  cloneVideo.addEventListener("content-card-carousel-visibility", sync);
  realVideo.addEventListener("play", sync);
  realVideo.addEventListener("pause", sync);
  observer?.observe(cloneVideo);
  sync();
  return () => {
    realVideo.removeEventListener("play", sync);
    realVideo.removeEventListener("pause", sync);
    observer?.unobserve(cloneVideo);
  };
}

/* Append a duplicate of every real card as DOM-only nodes, outside React's
 * VDOM, so a parent re-render cannot reconcile them away and the React tree
 * holds only real cards. The loop scrolls into them and `normalizeLoop`
 * jumps back by one period, which is invisible because each clone is a copy
 * of the card one period earlier. Clones are `aria-hidden` and every
 * focusable descendant takes `tabindex="-1"`, so neither assistive tech nor
 * Tab reaches a duplicate. Not `inert`: that would also block the pointer on
 * the clone's video button, which must keep working while it is on screen.
 * Returns the teardown. */
function insertCloneStrip(track: HTMLElement): () => void {
  const reals = Array.from(track.querySelectorAll<HTMLElement>(REAL_CARD_SELECTOR));
  const visible = new WeakMap<Element, boolean>();
  const observer = typeof IntersectionObserver === "undefined" ?
    null :
    new IntersectionObserver(entries => {
      entries.forEach(entry => {
        visible.set(entry.target, entry.isIntersecting);
        entry.target.dispatchEvent(new Event("content-card-carousel-visibility"));
      });
    }, { root: track });
  const teardowns = reals.map(real => {
    const clone = real.cloneNode(true) as HTMLElement;
    clone.setAttribute(CLONE_ATTR, "true");
    clone.setAttribute("aria-hidden", "true");
    clone.setAttribute("tabindex", "-1");
    clone
      .querySelectorAll<HTMLElement>("a, button, input, select, textarea, [tabindex]")
      .forEach(node => {
        node.setAttribute("tabindex", "-1");
      });
    const teardown = mirrorCloneVideo(real, clone, observer, visible);
    track.appendChild(clone);
    return teardown;
  });
  return () => {
    teardowns.forEach(teardown => teardown?.());
    observer?.disconnect();
    track.querySelectorAll(`[${CLONE_ATTR}="true"]`).forEach(node => node.remove());
  };
}

/**
 * The interactive shell around a Server-rendered strip of cards. It exists
 * because three things need runtime here:
 *
 * 1. Progress bar — how far the strip has scrolled, measured from the track.
 * 2. `←/→` keyboard navigation — the APG carousel pattern, which no native
 *    element covers on a plain `overflow-x: auto` container.
 * 3. Autoplay + pause — CSS cannot advance a scroller on a timer, and WCAG
 *    2.2.2 requires the pause control to actually pause it.
 *
 * The cards themselves cross the boundary as `children` — pre-rendered React
 * elements from the Server Component wrapper — so they ship no client JS.
 */
export interface ContentCardCarouselShellProps {
  children: React.ReactNode;
  cardCount: number;
  hideControls: boolean;
  autoplay: boolean;
  intervalMs: number;
  /**
   * When true, the strip loops continuously: after the last card the first
   * one slides in from the right, and Prev at the first card brings the last
   * one in from the left. A permanent copy of the real cards is appended to
   * the track once card widths are measurable (`insertCloneStrip`). Every
   * advance — arrow, keyboard, autoplay — is a plain one-card smooth scroll;
   * once `scrollLeft` is a full period into the copies, `normalizeLoop`
   * jumps back by that period with `behavior: "instant"`, which is
   * invisible. Prev at the start does the same jump forward before
   * scrolling back. Clones are `aria-hidden`, out of the Tab order and carry
   * `data-content-card-carousel-clone`; the live region announces the
   * logical card number.
   *
   * Default (on the Server Component wrapper) is `true`; `false` mounts no
   * clones and disables the arrows at the edges, as the Figma frame draws.
   */
  infiniteLoop: boolean;
  /** Analytics linkName for the Previous button. Nothing fires without it. */
  prevLinkName?: string;
  prevLinkPosition?: string;
  prevLinkDestination?: string;
  /** Analytics linkName for the Next button. Nothing fires without it. */
  nextLinkName?: string;
  nextLinkPosition?: string;
  nextLinkDestination?: string;
}

function ContentCardCarouselShellImpl({
  children,
  cardCount,
  hideControls,
  autoplay,
  intervalMs,
  infiniteLoop,
  prevLinkName,
  prevLinkPosition,
  prevLinkDestination,
  nextLinkName,
  nextLinkPosition,
  nextLinkDestination
}: ContentCardCarouselShellProps) {
  const trackId = useId();
  const trackRef = useRef<HTMLDivElement>(null);
  /* Discrete state so a swipe does not repaint the shell / TimeIndicator /
     two Buttons on every scroll frame. The float that a swipe actually
     moves — `scrollLeft / overflow` — lives in `progressRef` and is never
     rendered. `pageIndex` flips only when the reader crosses a card
     boundary; `atStart` / `atEnd` flip only at the two edges. Same shape as
     `rail-scroll.client.ts` — a ref holds the coordinate, state holds what
     the DOM shows. */
  const progressRef = useRef(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  /* Real scroll overflow measured from the track — powers the Next disabled
     state AND the whole indicator row's visibility (hidden when no overflow,
     UX ask). Default to `true` so SSR renders the indicator row at the
     reserved height — a false default would omit the row on the server and
     the ~36px row would appear on hydration, shifting everything below it
     (CLS regression flagged in PR review). The first `measure()` runs in
     the mount effect below and corrects to false on a `Few cards` desktop
     layout, which only ever shifts if there IS no overflow — i.e. nothing
     to navigate to anyway, so the shift costs nothing a reader cares about. */
  const [hasOverflow, setHasOverflow] = useState(true);
  /* Monotonic token tied to each "fresh animation start" — bumped by
     `step()` and `handleResume()` so the TimeIndicator's fill remounts in
     sync with the setInterval tick (not with pageIndex, which only flips
     when the smooth scroll crosses a rounding boundary — milliseconds AFTER
     the tick, leaving the fill drifting from the timer each cycle; PR
     review R1 §resetKey drift). Also bumped on Pause→Play transitions so
     the fill restarts from 0 together with the newly-created setInterval,
     fixing the central PR-review blocker where the fill resumed from the
     paused position while the timer restarted from scratch — bar hit 100%
     and wrapped before the actual advance. */
  const [animationToken, setAnimationToken] = useState(0);
  const bumpAnimation = useCallback(() => {
    setAnimationToken(t => t + 1);
  }, []);
  /* Read matchMedia at mount and hold it in state so the [autoplay] sync
     effect below can gate on it. Reading it inline would be a fresh miss
     per render; a one-shot ref would be missed by the sync. */
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  /* Two states, not one — a pause AT INIT (autoplay=false) reads the same as a
     user pause of an autoplaying carousel, and the two must not, because
     autoplay must never turn itself back on after a pause (WCAG 2.2.2). */
  const [running, setRunning] = useState(autoplay);
  const [interacted, setInteracted] = useState(false);
  /* Latched while our own `step()` is smoothly scrolling. The scroll listener
     ignores events during that window so an autoplay tick does not stop the
     autoplay it just triggered — only genuine swipe/wheel/drag counts. The
     timer id is kept so a follow-up tick can cancel the previous unlatch —
     otherwise an early timer fires DURING a later smooth-scroll and the
     listener reads the tail of the later scroll as a user swipe. */
  const programmaticScrollRef = useRef(false);
  const programmaticScrollTimerRef = useRef<number | null>(null);
  /* Teardown for the clone strip, set when `measure()` or the mount effect
   * inserts it. */
  const cloneTeardownRef = useRef<(() => void) | null>(null);

  /* Halts autoplay for the current sitting: keyboard nav, swipe, prev/next
     click and the pause button all call this. `interacted` blocks the timer
     from starting itself again on a state change; the reader has to press
     Play explicitly, which clears both flags in handleTogglePause. */
  const stopAutoplay = useCallback(() => {
    setInteracted(true);
    setRunning(false);
  }, []);

  /* If the parent flips `autoplay` after mount (usually a CMS re-render, not
     a runtime toggle), respect the new value — the initial useState reads
     autoplay once at mount and would otherwise be stale. A user's manual
     pause does NOT re-run this effect (autoplay prop itself is unchanged),
     so the pause stays honoured. ⚠ Gated on `prefersReducedMotion` — a
     prop-driven re-render must NOT restart the timer for a user who asked
     the system for no motion. */
  useEffect(() => {
    if (prefersReducedMotion) {
      setRunning(false);
      setInteracted(true);
      return;
    }
    setRunning(autoplay);
    setInteracted(false);
  }, [autoplay, prefersReducedMotion]);

  /* Pause → Play resync: when `running` goes from false to true, bump the
   * animation token so the TimeIndicator's fill remounts and starts from 0
   * at the same instant the autoplay effect below (re)creates its
   * setInterval with a FULL `safeInterval` duration. Without this, the fill
   * resumes from its paused position via `animation-play-state`, while the
   * timer starts from scratch — the bar hits 100% and the keyframe wraps
   * before the actual advance, misreporting remaining time for the rest of
   * that card. This is the central PR-review blocker. The ref holds the
   * previous value so the effect only fires on the false→true edge, not on
   * every re-render. */
  const prevRunningRef = useRef(running);
  useEffect(() => {
    if (!prevRunningRef.current && running) {
      bumpAnimation();
    }
    prevRunningRef.current = running;
  }, [running, bumpAnimation]);

  /* Measures scroll and writes the DISCRETE values that the JSX reads:
     `pageIndex`, `atStart`, `atEnd`, `hasOverflow`. Each `setState` here
     compares against the previous value and skips the update when it has
     not changed, so a swipe (which fires this per scroll frame) re-renders
     only when the reader crosses a card boundary or hits an edge — not on
     every pixel. The raw float sits in `progressRef` for any consumer that
     needs sub-pixel precision (none today; the ref is here so we cannot
     regress by adding one that reads state). */
  const measure = useCallback(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    /* Lazy clone insertion — once offsetWidth is readable (after layout,
     * or after a test stubs it) ensure the permanent clone strip is
     * present. No-op if already inserted. Keeps jsdom tests that don't
     * stub `offsetWidth` free of clones (preserves existing card counts). */
    if (
      infiniteLoop &&
      cardCount > 1 &&
      !cloneTeardownRef.current &&
      getCardWidth(track) > 0
    ) {
      cloneTeardownRef.current = insertCloneStrip(track);
    }
    /* With clones mounted `scrollWidth` covers both copies, so rebuild the
       width the real strip alone would scroll: its padding, the cards and
       the gaps between them (no trailing gap). Without clones `scrollWidth`
       is already that. */
    const period = getLoopPeriod(track);
    let realStripWidth = track.scrollWidth;
    if (period > 0) {
      const style = getComputedStyle(track);
      const gap = parseFloat(style.columnGap || "0") || 0;
      const paddingX = (parseFloat(style.paddingLeft || "0") || 0) +
        (parseFloat(style.paddingRight || "0") || 0);
      realStripWidth = paddingX + period - gap;
    }

    const overflow = realStripWidth - track.clientWidth;
    /* Edge-fade switches: written to the DOM, not state, because they only
       feed CSS. They read the PHYSICAL scrollLeft — inside the clone strip
       there are cards to the left even where the logical position is 0 —
       and with clones mounted the strip never ends on the right. */
    track.toggleAttribute("data-at-start", overflow <= 0 || track.scrollLeft < 1);
    track.toggleAttribute(
      "data-at-end",
      overflow <= 0 || (period <= 0 && track.scrollLeft >= overflow - 1)
    );
    if (overflow <= 0) {
      progressRef.current = 0;
      setHasOverflow(false);
      setPageIndex(0);
      setAtStart(true);
      setAtEnd(false);
      return;
    }
    /* Logical scroll — fold a scrollLeft inside the clone strip back onto
       its equivalent in the real strip. `((x % n) + n) % n` is the safe
       modulo in case scrollLeft is a hair negative on an elastic bounce. */
    const logicalScroll = period > 0 ?
      ((track.scrollLeft % period) + period) % period :
      track.scrollLeft;
    const progress = Math.max(0, Math.min(1, logicalScroll / overflow));
    progressRef.current = progress;
    /* The functional form + strict-equality guard is what keeps a swipe
       from re-rendering the tree on every frame — React bails when the
       returned value equals the previous. */
    const nextPage = Math.min(
      cardCount - 1,
      Math.round(progress * (cardCount - 1))
    );
    setHasOverflow(true);
    setPageIndex(prev => (prev === nextPage ? prev : nextPage));
    setAtStart(prev => {
      const next = progress <= 1e-3;
      return prev === next ? prev : next;
    });
    setAtEnd(prev => {
      const next = progress >= 1 - 1e-3;
      return prev === next ? prev : next;
    });
  }, [cardCount, infiniteLoop]);

  /* Closes the loop: once `scrollLeft` is a full period into the clone strip,
     jump back by one period. Before and after are pixel-identical (each
     clone copies the card one period earlier), and `behavior: "instant"`
     beats the stylesheet's `scroll-behavior: smooth`. Runs when a step
     settles and when a user scroll ends. The latch drains the jump's own
     scroll event, so autoplay does not read it as a swipe; it is left to a
     step that started in the meantime. */
  const normalizeLoop = useCallback(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    const period = getLoopPeriod(track);
    if (period <= 0 || track.scrollLeft < period) {
      return;
    }
    programmaticScrollRef.current = true;
    track.scrollTo({
      left: track.scrollLeft - period,
      behavior: "instant"
    });
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (programmaticScrollTimerRef.current === null) {
          programmaticScrollRef.current = false;
        }
      });
    });
  }, []);

  /* Advance by one card width. Reads the FIRST card in the strip for its
     rendered width plus the gap — resize-safe, and does not need a prop. */
  const step = useCallback((direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    const cardWidth = getCardWidth(track);
    if (cardWidth <= 0) {
      return;
    }
    const delta = cardWidth * direction;
    /* Behaviour follows prefers-reduced-motion — the CSS `scroll-behavior:
       auto` fallback in the module only takes effect when scroll-behaviour is
       NOT explicitly set on the scroll call. Passing "smooth" here would
       override that fallback and animate for a user who asked the system to
       stop. Reads state (set once at mount) rather than matchMedia inline —
       one source of truth, and the [autoplay] sync uses the same value. */
    /* Latch the flag BEFORE scrollBy — the browser can fire the first scroll
       event on the same tick. Cancel a previous latch's timer first so a
       tick fired within 500ms of the last one does not unlatch the fresh
       smooth-scroll early and cause the listener to read its tail as a
       user swipe. 500ms covers the smooth-scroll duration on every browser
       (spec doesn't pin it; ~300-400ms is typical). */
    programmaticScrollRef.current = true;
    if (programmaticScrollTimerRef.current !== null) {
      window.clearTimeout(programmaticScrollTimerRef.current);
    }
    programmaticScrollTimerRef.current = window.setTimeout(() => {
      programmaticScrollRef.current = false;
      programmaticScrollTimerRef.current = null;
      normalizeLoop();
    }, 500);
    /* Loop edges, before the smooth scroll. Forward from inside the clone
       strip (a swipe that ended there before its jump-back ran): jump back
       first, or `scrollBy` would stop at the end of the copies. Backward at
       the start: jump one period into the copies first, so the last card
       comes in from the left. Both jumps are pixel-identical frames. */
    const period = getLoopPeriod(track);
    const start = track.scrollLeft;
    let jump = 0;
    if (period > 0) {
      jump = direction === 1 && start >= period ? -period :
        direction === -1 && start < 1 ? period :
          0;
      if (jump !== 0) {
        track.scrollTo({
          left: start + jump,
          behavior: "instant"
        });
      }
    }
    /* Resync the TimeIndicator fill with the newly-set timer by remounting
     * at the moment of the advance — not later, when the scroll crosses a
     * card boundary and flips `pageIndex`. Keeping these two clocks in step
     * is the fix for the PR-review drift finding. */
    bumpAnimation();
    const behavior = prefersReducedMotion ? "auto" : "smooth";
    if (jump !== 0) {
      /* iOS Safari runs a `scrollBy` issued in the same task as the jump from
         the PRE-jump position, so Prev at the first card lands at -1 card and
         the strip paints blank. An absolute target is unaffected. */
      track.scrollTo({
        left: start + jump + delta,
        behavior
      });
    } else {
      track.scrollBy({
        left: delta,
        behavior
      });
    }
  }, [prefersReducedMotion, bumpAnimation, normalizeLoop]);

  /* Clone strip — inserted here if the first card is already measurable;
   * otherwise `measure()` inserts it once `offsetWidth` is readable. Torn
   * down on unmount and when the loop is switched off. */
  useEffect(() => {
    const track = trackRef.current;
    if (track && infiniteLoop && cardCount > 1 && !cloneTeardownRef.current &&
      getCardWidth(track) > 0) {
      cloneTeardownRef.current = insertCloneStrip(track);
    }
    return () => {
      cloneTeardownRef.current?.();
      cloneTeardownRef.current = null;
    };
  }, [infiniteLoop, cardCount]);

  const handlePrev = useCallback(() => {
    /* Analytics fire only when the caller supplied a linkName — nothing is
     * tracked otherwise, per the fireLinkClick contract. linkPosition
     * defaults to the DDS body position; linkDestination is omitted from the
     * event when falsy. */
    if (prevLinkName) {
      fireLinkClick({
        linkName: prevLinkName,
        linkPosition: prevLinkPosition || LINK_POSITION_BODY,
        linkDestination: prevLinkDestination
      });
    }
    stopAutoplay();
    step(-1);
  }, [step, stopAutoplay, prevLinkName, prevLinkPosition, prevLinkDestination]);

  const handleNext = useCallback(() => {
    if (nextLinkName) {
      fireLinkClick({
        linkName: nextLinkName,
        linkPosition: nextLinkPosition || LINK_POSITION_BODY,
        linkDestination: nextLinkDestination
      });
    }
    stopAutoplay();
    step(1);
  }, [step, stopAutoplay, nextLinkName, nextLinkPosition, nextLinkDestination]);

  /* Actual toggle: Pause halts the tick AND marks as interacted (so a swipe
   * or arrow-key won't restart it either). Play is an explicit resume — it
   * clears both flags so the useEffect below runs the tick again. WCAG 2.2.2
   * needs the pause to hold; it does not forbid a manual resume.
   * ⚠ The DOM rewind sits OUTSIDE the setState calls. React updaters must be
   * pure — StrictMode double-invokes them, and concurrent rendering may
   * discard and re-run one; a DOM write inside an updater would then double
   * or vanish. `runningRef` gives us the current value so we can branch on it
   * without reading state through an updater. */
  const runningRef = useRef(running);
  runningRef.current = running;
  const handleTogglePause = useCallback(() => {
    const wasRunning = runningRef.current;
    if (!wasRunning) {
      /* Play from the END would otherwise advertise "Pause autoplay" for a
         full interval and never scroll — the tick effect exits at
         `scrollLeft >= maxScroll - 1` before ever calling step(). Act on
         the end position:
           - Under `infiniteLoop`: nothing to do. The next tick steps into
             the clone strip and `normalizeLoop` closes the loop.
           - Else: instant rewind to 0, the Figma `D-last card`
             frame UX. `behavior: "instant"` because `.track` sets
             `scroll-behavior: smooth`; "auto" would resolve to smooth and
             animate the rewind visibly. */
      const track = trackRef.current;
      if (track && !infiniteLoop) {
        const maxScroll = track.scrollWidth - track.clientWidth;
        if (maxScroll > 0 && track.scrollLeft >= maxScroll - 1) {
          track.scrollTo({
            left: 0,
            behavior: "instant"
          });
        }
      }
    }
    setInteracted(wasRunning);
    setRunning(!wasRunning);
  }, [infiniteLoop]);

  /* Keyboard nav (APG carousel): `←` and `→` while focus is inside the track
     scroll it by one card. Any other key falls through to the browser, so
     Tab still moves focus onto the CTAs inside a card. */
  const onTrackKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowRight") {
        event.preventDefault();
        stopAutoplay();
        step(1);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        stopAutoplay();
        step(-1);
      }
    },
    [step, stopAutoplay]
  );

  /* Progress bar updates on scroll AND on viewport resize (a wider viewport
     shrinks the overflow at the same scrollLeft, so the ratio moves). A
     scroll not driven by our own step() (swipe, wheel, drag) also halts
     autoplay — the docs promise a swipe stops the tick, and only halting on
     click was leaving that promise unmet. */
  const autoplayingRef = useRef(false);
  autoplayingRef.current = running && !interacted;
  useEffect(() => {
    const track = trackRef.current;
    if (!track) {
      return undefined;
    }
    measure();
    let lastLeft = track.scrollLeft;
    /* A swipe, wheel or drag can end inside the clone strip, and at the end
       of the copies it would stop dead. Close the loop once the user's
       scroll ends: on `scrollend` where the browser has it, else after
       150ms without a scroll event. Our own steps normalize from their
       settle timer, so latched scrolls are skipped here. */
    const hasScrollEnd = "onscrollend" in window;
    let settleTimer: number | null = null;
    const onScrollEnd = () => {
      settleTimer = null;
      if (!programmaticScrollRef.current) {
        normalizeLoop();
      }
    };
    const onScroll = () => {
      measure();
      /* Any user-driven scroll while autoplay is running counts as
         interaction. Scrolls our own code starts — steps and loop jumps —
         set `programmaticScrollRef` first and are ignored. The 4px floor
         drops sub-pixel snap settling. */
      const delta = Math.abs(track.scrollLeft - lastLeft);
      lastLeft = track.scrollLeft;
      if (autoplayingRef.current && !programmaticScrollRef.current && delta > 4) {
        stopAutoplay();
      }
      if (!hasScrollEnd && !programmaticScrollRef.current) {
        if (settleTimer !== null) {
          window.clearTimeout(settleTimer);
        }
        settleTimer = window.setTimeout(onScrollEnd, 150);
      }
    };
    const onResize = () => measure();
    track.addEventListener("scroll", onScroll, { passive: true });
    if (hasScrollEnd) {
      track.addEventListener("scrollend", onScrollEnd);
    }
    window.addEventListener("resize", onResize);
    return () => {
      track.removeEventListener("scroll", onScroll);
      track.removeEventListener("scrollend", onScrollEnd);
      window.removeEventListener("resize", onResize);
      if (settleTimer !== null) {
        window.clearTimeout(settleTimer);
      }
    };
  }, [measure, stopAutoplay, normalizeLoop]);

  /* Autoplay tick. Stops permanently on user interaction (WCAG 2.2.2 pause);
     at the end of the strip it wraps back to the first card so the loop
     continues (UX ask — the reader who lets autoplay run should not be
     stranded on the last card).
     ⚠ `hideControls` removes the pause button and the arrow buttons, leaving
     the reader with only keyboard nav to halt the motion. WCAG 2.2.2 needs a
     visible or pointer-reachable pause, so autoplay is REFUSED entirely under
     that combination — a non-default configuration that the docs warn about. */
  useEffect(() => {
    if (!running || interacted || hideControls) {
      return undefined;
    }
    /* `setInterval(0)` (or any negative) creates a tight loop on layout
       reads and scroll calls — clamp to a minimum a reader could plausibly
       follow. 100 ms is the WCAG-recommended floor for auto-advancing
       content. `Number.isFinite` catches NaN and Infinity. */
    const safeInterval =
      Number.isFinite(intervalMs) ? Math.max(100, intervalMs) : 5000;
    const id = window.setInterval(() => {
      const track = trackRef.current;
      if (!track) {
        return;
      }
      /* Skip ticks while a programmatic scroll (a step or a loop jump) is
         still latched, or the tick re-enters and bumps the fill twice. */
      if (programmaticScrollRef.current) {
        return;
      }
      /* Under `infiniteLoop` every tick is a plain `step(1)`, the last-to-
       * first one included, so every gap is the same; the step's settle
       * timer closes the loop. Without it the tick rewinds smoothly to the
       * first card at the end. */
      if (!infiniteLoop) {
        const effectiveMax = track.scrollWidth - track.clientWidth;
        if (effectiveMax > 0 && track.scrollLeft >= effectiveMax - 1) {
          programmaticScrollRef.current = true;
          if (programmaticScrollTimerRef.current !== null) {
            window.clearTimeout(programmaticScrollTimerRef.current);
          }
          programmaticScrollTimerRef.current = window.setTimeout(() => {
            programmaticScrollRef.current = false;
            programmaticScrollTimerRef.current = null;
          }, 2000);
          bumpAnimation();
          track.scrollTo({
            left: 0,
            behavior: prefersReducedMotion ? "auto" : "smooth"
          });
          return;
        }
      }
      step(1);
    }, safeInterval);
    return () => window.clearInterval(id);
  }, [running, interacted, intervalMs, step, hideControls, prefersReducedMotion, bumpAnimation, infiniteLoop]);

  /* Respect prefers-reduced-motion: never start autoplay for a user who has
     asked the system to reduce motion. Sets state once at mount so the
     [autoplay] sync effect above and `step()` below both read the same
     value — an inline matchMedia would let a later autoplay prop change
     restart the timer for the same user (Suraj's Should-fix 4). Also
     clears the programmatic-scroll timer on unmount. */
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setPrefersReducedMotion(true);
    }
    return () => {
      if (programmaticScrollTimerRef.current !== null) {
        window.clearTimeout(programmaticScrollTimerRef.current);
      }
    };
  }, []);

  return (
    <div className={styles.shell}>
      <div
        aria-label="Content cards"
        aria-live="off"
        aria-roledescription="carousel"
        className={styles.track}
        /* Server default only — `measure()` toggles it from then on. Present
           at first paint so the left fade does not show and snap off. */
        data-at-start=""
        id={trackId}
        onKeyDown={onTrackKeyDown}
        ref={trackRef}
        role="group"
        /* tabIndex 0 so the whole strip can accept the arrow-key focus even
           when the first interactive child (a CTA) is not the target. */
        tabIndex={0}
      >
        {children}
      </div>

      {/* APG carousel live region: announces the current slide to a screen
          reader on page change. `aria-live` flips to "off" while autoplay is
          running (the reader has not asked to be told, and constant
          announcements would flood them), and to "polite" the moment they
          pause — the pause is the ask. Rendered only when the strip has room
          to scroll; without overflow there is no navigation to announce. */}
      {hasOverflow ? (
        <span
          aria-live={running ? "off" : "polite"}
          className="dds-sr-only"
          role="status"
        >
          {`Card ${pageIndex + 1} of ${cardCount}`}
        </span>
      ) : null}

      {/* Hide the indicator row when the strip has no room to scroll — a
          `Few cards` layout on desktop/tablet where every card is visible has
          nothing to navigate to and nothing to time (UX ask). `hasOverflow`
          is measured on the client after mount, so a wide viewport with few
          cards will render this branch empty on the initial paint too.
          On mobile the flex-basis math keeps ~1.1 cards visible even for a
          two-card set, so `hasOverflow` stays true there and the row shows. */}
      {hideControls || !hasOverflow ? null : (
        <div className={styles.indicator}>
          {/* Prev/next arrows + the Time Indicator bar, laid out in a single
              <nav> so a screen reader hits them as one landmark named
              "Content cards". Arrows are the DDS Button atom (tertiary
              on-dark) so their disabled/hover/focus states, click analytics
              and keyboard behaviour come from the atom rather than being
              rebuilt here. The centre column stretches (flex-grow slot) so
              the Time Indicator bar has room to extend.

              Under `infiniteLoop` (default) the arrows never disable: Next
              at the last card steps into the clone strip, Prev at the
              first jumps into it and steps back. Under `!infiniteLoop`
              they disable at the respective edge, matching the Figma
              frame's `D-last card` symbol. */}
          <nav aria-label="Content cards" className={styles.controlsNav}>
            <Button
              ariaLabel="Previous card"
              disabled={!infiniteLoop && atStart}
              onClick={handlePrev}
              size="small"
              startIcon={
                <img
                  alt=""
                  aria-hidden="true"
                  height={24}
                  src={!infiniteLoop && atStart ? PREV_ICON_DISABLED : PREV_ICON_ENABLED}
                  width={24}
                />
              }
              variant="tertiary"
            />
            <div className={styles.timeIndicatorSlot}>
              <TimeIndicator
                intervalMs={intervalMs}
                /* `animationToken` — bumped by `step()`, the end-wrap and
                   the Pause→Play effect — keeps the fill's keyframe clock
                   in step with the setInterval tick. Using `pageIndex`
                   here instead drifts: pageIndex only flips when the smooth
                   scroll crosses a rounding boundary, milliseconds after
                   the tick fires. Addresses PR-review resetKey-drift and
                   Pause/Play-desync findings together. */
                resetKey={animationToken}
                /* Only "running" when autoplay is actually ticking. The
                   tick effect below refuses to run under `interacted` or
                   `hideControls`, and `hideControls` is already false in
                   this branch — so keep the two checks that can flip at
                   runtime and drop the constant one. */
                running={running && !interacted}
                /* UX ask: use the 4px (small) variant on tablet/mobile where
                   the indicator row sits in a narrow column, and the 6px
                   (large) only on desktop ≥1280px. `responsive` flips the
                   height at the DDS 1280 breakpoint via the molecule's own
                   media query — the carousel does not own that breakpoint
                   logic itself. */
                size="responsive"
              />
            </div>
            <Button
              ariaLabel="Next card"
              disabled={!infiniteLoop && atEnd}
              onClick={handleNext}
              size="small"
              startIcon={
                <img
                  alt=""
                  aria-hidden="true"
                  height={24}
                  src={!infiniteLoop && atEnd ? NEXT_ICON_DISABLED : NEXT_ICON_ENABLED}
                  width={24}
                />
              }
              variant="tertiary"
            />
          </nav>
          {/* Always shown — click always toggles the autoplay tick. Starting
              state comes from the `autoplay` prop, but a reader can start it
              themselves by pressing Play. See handleTogglePause for the
              interacted-flag mechanics that keep WCAG 2.2.2 honest. */}
          <button
            aria-controls={trackId}
            /* Action-describing label. Combined with `aria-pressed` it
               announced as "Play autoplay, pressed" — confusing. Dropping
               the pressed state and leaving the label describe the action
               is the YouTube-style pattern screen readers handle cleanly. */
            aria-label={running ? "Pause autoplay" : "Play autoplay"}
            className={styles.pauseButton}
            onClick={handleTogglePause}
            type="button"
          >
            {running ? <PauseGlyph /> : <PlayGlyph />}
          </button>
        </div>
      )}
    </div>
  );
}
export const ContentCardCarouselShell = withSafeRender(ContentCardCarouselShellImpl, "ContentCardCarouselShell");

/* ---- inline pause glyphs -------------------------------------------------
   Small enough to keep out of the atoms tree — the pause control is their
   only caller. Prev/next chevrons live in this file's PREV_/NEXT_ICON_*
   constants (top of file), fed to the DDS Button atom as `startIcon`. */

function PauseGlyph() {
  return (
    <svg
      aria-hidden="true"
      className={styles.glyph}
      focusable="false"
      viewBox="0 0 20 20"
    >
      <rect fill="currentColor" height="12" rx="1" width="3" x="5" y="4" />
      <rect fill="currentColor" height="12" rx="1" width="3" x="12" y="4" />
    </svg>
  );
}

function PlayGlyph() {
  return (
    <svg
      aria-hidden="true"
      className={styles.glyph}
      focusable="false"
      viewBox="0 0 20 20"
    >
      <path
        d="M6 4v12l10-6z"
        fill="currentColor"
      />
    </svg>
  );
}

/* ---- video with pause overlay ------------------------------------------
   A muted, looping autoplay video with a small tonal pause pill over the
   top-right of the media slot. Its OWN client boundary because two things
   here need runtime: reading paused state from the <video> element and
   toggling it. Autoplay muted is universally allowed; the pause makes the
   loop stoppable (WCAG 2.2.2 for the auto-playing media itself, separate
   from the carousel's autoplay tick). */

export interface VideoWithPauseProps {
  src: string;
  /** Accessible label context — the pause button reads "Pause `title`". */
  title: string;
  /** Optional still image shown before the video loads or plays. Doubles as
   * a fallback for blocked-autoplay so a paused card is never blank. */
  poster?: string;
  /** Alt text for the poster when it stands in for the video. Passed to the
   * <img> during the poster-only phase (video's native `poster` doesn't
   * expose an alt attribute). */
  posterAlt?: string;
  className?: string;
  pauseClassName?: string;
}

function VideoWithPauseImpl({
  src,
  title,
  poster,
  posterAlt,
  className,
  pauseClassName
}: VideoWithPauseProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  /* Assumed paused until the element reports otherwise — a blocked-autoplay
     (unfocused tab, low battery) would otherwise leave the button labelled
     "Pause" over a still frame. The play/pause event listeners below flip
     this to match the element's actual state. */
  const [paused, setPaused] = useState(true);

  const toggle = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (video.paused) {
      /* play() returns a promise; a rejection (autoplay policy) leaves the
         previous state — no throw at the user, the event listener will
         reconcile if playback ever succeeds. */
      const playResult = video.play();
      /* jsdom's `HTMLMediaElement.play()` returns undefined; a real browser
         returns a Promise. Only chain `.catch` when the runtime handed us
         something Promise-like — otherwise the call throws under jest. */
      if (playResult && typeof (playResult as Promise<void>).catch === "function") {
        (playResult as Promise<void>).catch(() => undefined);
      }
    } else {
      video.pause();
    }
  }, []);

  /* Reduced motion: never autoplay for a user who asked the system for it.
     Both the reduced-motion check and the play/pause event listeners live
     in this effect so the paused-state is truthful before the first paint. */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return undefined;
    }
    const reducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const onPlay = () => setPaused(false);
    const onPause = () => setPaused(true);
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    if (!reducedMotion) {
      const playResult = video.play();
      /* jsdom's `HTMLMediaElement.play()` returns undefined; a real browser
         returns a Promise. Only chain `.catch` when the runtime handed us
         something Promise-like — otherwise the call throws under jest. */
      if (playResult && typeof (playResult as Promise<void>).catch === "function") {
        (playResult as Promise<void>).catch(() => undefined);
      }
    }
    return () => {
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
    };
  }, []);

  return (
    <>
      {/* Native `poster` on the <video> element accepts a URL but no alt —
          when the reader has an image alt, render it as a real <img> before
          the video takes over on `play`. Once the video plays, the frame
          covers it. */}
      {poster && paused ? (
        <img
          alt={posterAlt ?? ""}
          className={className}
          loading="lazy"
          src={poster}
        />
      ) : null}
      <video
        aria-hidden="true"
        className={className}
        loop
        muted
        playsInline
        poster={poster}
        ref={videoRef}
        src={src}
      />
      <button
        /* Action-describing label without aria-pressed — same reasoning as
           the carousel pause: the pair announces confusingly. */
        aria-label={paused ? `Play ${title} video` : `Pause ${title} video`}
        className={pauseClassName}
        onClick={toggle}
        type="button"
      >
        {paused ? <PlayGlyphSmall /> : <PauseGlyphSmall />}
      </button>
    </>
  );
}
export const VideoWithPause = withSafeRender(VideoWithPauseImpl, "VideoWithPause");

function PauseGlyphSmall() {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 16 16">
      <rect fill="currentColor" height="10" rx="0.75" width="2.5" x="4" y="3" />
      <rect fill="currentColor" height="10" rx="0.75" width="2.5" x="9.5" y="3" />
    </svg>
  );
}

function PlayGlyphSmall() {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 16 16">
      <path d="M5 3v10l8-5z" fill="currentColor" />
    </svg>
  );
}

export default ContentCardCarouselShell;
